import { useState } from 'react';
import { add, getLookupTypes, Lookup, LookupSchema } from '@/api/admin/lookup';
import { Button } from '@/components/ui/button';
import { ComboboxSelect } from '@/components/ui/custom/combobox-select';
import {
	Form,
	FormControl,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import {
	Select,
	SelectContent,
	SelectGroup,
	SelectItem,
	SelectLabel,
	SelectTrigger,
	SelectValue,
} from '@/components/ui/select';
import {
	Sheet,
	SheetContent,
	SheetDescription,
	SheetHeader,
	SheetTitle,
} from '@/components/ui/sheet';
import {
	Tooltip,
	TooltipContent,
	TooltipProvider,
	TooltipTrigger,
} from '@/components/ui/tooltip';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CirclePlus } from 'lucide-react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { toast } from 'sonner';

export function AddLookup({ importTypeId }: { importTypeId: number }) {
	const databaseInstances: Array<{ id: number; name: string }> = [
		{ id: 0, name: 'All' },
		{ id: 1, name: 'PIPS' },
		{ id: 2, name: 'AIM ECC' },
		{ id: 3, name: 'AIM RLA' },
		{ id: 4, name: 'AIM South & Western' },
		{ id: 5, name: 'AIM Texas Security General' },
	];

	const { data: lookupTypes, isLoading: isLoadingLookupTypes } = useQuery({
		queryKey: [`lookup-types-${importTypeId}`],
		queryFn: () => getLookupTypes(importTypeId),
		staleTime: 5 * 60 * 1000, // Cache for 5 minutes
	});

	const [open, setOpen] = useState(false);
	const [isOtherSelected, setIsOtherSelected] = useState(false);
	const queryClient = useQueryClient();

	const { mutate } = useMutation({
		mutationFn: (newLookup: Lookup) => add(newLookup),
		onSuccess: () => {
			toast.success(`Success! Lookup has been successfully added.`, {
				dismissible: true,
			});
			setOpen(false);
			queryClient.invalidateQueries({
				queryKey: [`lookups-${importTypeId}`],
			});
			reset();
			setIsOtherSelected(false);
		},
		onError: () => {
			toast.error(`Error! Lookup could not be added.`, {
				dismissible: true,
			});
		},
	});

	const form = useForm<Lookup>({
		resolver: zodResolver(LookupSchema),
		mode: 'all',
		defaultValues: {
			importTypeId: importTypeId,
			databaseInstanceId: 0,
			expired: false,
		},
	});

	const { reset } = form;
	const onSubmit: SubmitHandler<Lookup> = (values: Lookup) => mutate(values);

	return (
		<div>
			<TooltipProvider>
				<Tooltip>
					<TooltipTrigger asChild>
						<Button className="m-auto" onClick={() => setOpen(true)}>
							<CirclePlus />
							Add New Lookup
						</Button>
					</TooltipTrigger>
					<TooltipContent>
						<p>Add a new lookup</p>
					</TooltipContent>
				</Tooltip>
			</TooltipProvider>

			<Sheet open={open} onOpenChange={(open) => setOpen(open)}>
				<SheetContent>
					<SheetHeader>
						<SheetTitle>Add a New Lookup</SheetTitle>
						<SheetDescription></SheetDescription>
					</SheetHeader>
					<Form {...form}>
						<form onSubmit={form.handleSubmit(onSubmit)}>
							<div className="space-y-4">
								<FormField
									control={form.control}
									name="importTypeId"
									render={({ field }) => (
										<FormItem>
											<FormLabel>Import Type ID</FormLabel>
											<FormControl>
												<Input {...field} value={field.value ?? ''} disabled={true} />
											</FormControl>
											<FormMessage />
										</FormItem>
									)}
								/>
								<FormField
									control={form.control}
									name="lookupType"
									render={({ field }) => (
										<ComboboxSelect
											field={field}
											options={lookupTypes}
											isLoading={isLoadingLookupTypes}
											label="Lookup Type"
											placeholder="Select lookup type..."
											searchPlaceholder="Search lookup types..."
											required={true}
											includeOtherOption={true}
											onOtherSelected={setIsOtherSelected}
											isOtherSelected={isOtherSelected}
											emptyMessage="No lookup type found."
											loadingMessage="Loading lookup types..."
										/>
									)}
								/>
								{isOtherSelected && (
									<FormField
										control={form.control}
										name="lookupType"
										render={({ field }) => (
											<FormItem>
												<FormLabel>
													New Lookup Type <span className="text-red-500">*</span>
												</FormLabel>
												<FormControl>
													<Input
														{...field}
														value={field.value ?? ''}
														placeholder="Enter new lookup type..."
													/>
												</FormControl>
												<FormMessage />
											</FormItem>
										)}
									/>
								)}
								<FormField
									control={form.control}
									name="lookupValue"
									render={({ field }) => (
										<FormItem>
											<FormLabel>
												Lookup Value<span className="text-red-500">*</span>
											</FormLabel>
											<FormControl>
												<Input {...field} value={field.value ?? ''} />
											</FormControl>
											<FormMessage />
										</FormItem>
									)}
								/>
								<FormField
									control={form.control}
									name="sourceValue"
									render={({ field }) => (
										<FormItem>
											<FormLabel>
												Source Value<span className="text-red-500">*</span>
											</FormLabel>
											<FormControl>
												<Input {...field} value={field.value ?? ''} />
											</FormControl>
											<FormMessage />
										</FormItem>
									)}
								/>
								<FormField
									control={form.control}
									name="lookupDescription"
									render={({ field }) => (
										<FormItem>
											<FormLabel>Lookup Description</FormLabel>
											<FormControl>
												<Input {...field} value={field.value ?? ''} />
											</FormControl>
											<FormMessage />
										</FormItem>
									)}
								/>
								<FormField
									control={form.control}
									name="optionalLookupValue"
									render={({ field }) => (
										<FormItem>
											<FormLabel>Optional Lookup Value</FormLabel>
											<FormControl>
												<Input {...field} value={field.value ?? ''} />
											</FormControl>
											<FormMessage />
										</FormItem>
									)}
								/>
								<FormField
									control={form.control}
									name="optionalLookupValueB"
									render={({ field }) => (
										<FormItem>
											<FormLabel>Optional Lookup Value B</FormLabel>
											<FormControl>
												<Input {...field} value={field.value ?? ''} />
											</FormControl>
											<FormMessage />
										</FormItem>
									)}
								/>
								<FormField
									control={form.control}
									name="databaseInstanceId"
									render={({ field }) => (
										<FormItem>
											<FormLabel>Database Instance</FormLabel>
											<FormControl>
												<Select
													value={field.value?.toString() ?? '0'}
													onValueChange={(val) => {
														const numValue = parseInt(val, 10);
														field.onChange(isNaN(numValue) ? 0 : numValue);
													}}
												>
													<SelectTrigger>
														<SelectValue placeholder="Select a field..." />
													</SelectTrigger>
													<SelectContent>
														<SelectGroup>
															<SelectLabel>Database Instances</SelectLabel>
															{databaseInstances!.map((field) => (
																<SelectItem key={field.id} value={field.id.toString()}>
																	{field.name}
																</SelectItem>
															))}
														</SelectGroup>
													</SelectContent>
												</Select>
											</FormControl>
											<FormMessage />
										</FormItem>
									)}
								/>
							</div>

							<div className="mt-9 flex justify-end">
								<Button
									type="submit"
									disabled={!form.formState.isValid || form.formState.isSubmitting}
								>
									{form.formState.isSubmitting ? 'Adding...' : 'Add'}
								</Button>
							</div>
						</form>
					</Form>
				</SheetContent>
			</Sheet>
		</div>
	);
}
